/**
 * The signed-in session as Firebase left it in this browser, read before the Firebase
 * SDK has finished starting.
 *
 * On a repeat visit the SDK reloads the persisted user from Google (an "accounts:lookup"
 * round trip) before it tells the app anyone is signed in. The persisted token is already
 * on the device, so the app reads it directly and starts the profile and product requests
 * at once. The SDK's verdict still arrives: if the lookup fails or the account changed,
 * useAuth tears the session down.
 *
 * Parsing and claim decoding are separate from the IndexedDB read so they can be tested
 * under jsdom, which has no IndexedDB.
 */

export interface PersistedSession {
  uid: string;
  accessToken: string;
  /** Epoch ms after which the token is no longer usable. */
  expirationTime: number;
}

/** Do not use a token this close to its expiry; the SDK will mint a fresh one. */
const EXPIRY_MARGIN_MS = 60 * 1000;

/** Parses Firebase's persisted user record, whichever store it came from. */
export function parsePersistedUser(value: unknown, now = Date.now()): PersistedSession | undefined {
  const v = value as { uid?: unknown; stsTokenManager?: { accessToken?: unknown; expirationTime?: unknown } } | null;
  const uid = v?.uid;
  const accessToken = v?.stsTokenManager?.accessToken;
  const expirationTime = Number(v?.stsTokenManager?.expirationTime);
  if (typeof uid !== 'string' || typeof accessToken !== 'string' || !Number.isFinite(expirationTime)) return undefined;
  if (expirationTime - EXPIRY_MARGIN_MS <= now) return undefined;
  return { uid, accessToken, expirationTime };
}

/** The raw claims object under a namespace in a JWT's payload, without verifying it.
 *  Verification is Hasura's job on every request; here the claims only decide what to
 *  ask for first. */
export function decodeJwtClaims(jwt: string, namespace: string): Record<string, unknown> | undefined {
  const parts = jwt.split('.');
  if (parts.length < 2) return undefined;
  try {
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    const ns = payload?.[namespace];
    return ns && typeof ns === 'object' ? (ns as Record<string, unknown>) : undefined;
  } catch {
    return undefined;
  }
}

export function persistedUserKey(apiKey: string): string {
  return `firebase:authUser:${apiKey}:[DEFAULT]`;
}

/** Read Firebase's persisted user from IndexedDB (its default web persistence), falling
 *  back to localStorage (its second choice). Resolves undefined when nothing is stored
 *  or the store cannot be opened; never throws. */
export async function readPersistedUser(apiKey: string): Promise<unknown> {
  const key = persistedUserKey(apiKey);
  const fromIdb = await readIndexedDb(key);
  if (fromIdb !== undefined) return fromIdb;
  try {
    const raw = typeof localStorage === 'undefined' ? null : localStorage.getItem(key);
    return raw ? JSON.parse(raw) : undefined;
  } catch {
    return undefined;
  }
}

function readIndexedDb(key: string): Promise<unknown> {
  return new Promise(resolve => {
    try {
      if (typeof indexedDB === 'undefined') return resolve(undefined);
      const open = indexedDB.open('firebaseLocalStorageDb');
      open.onerror = () => resolve(undefined);
      open.onupgradeneeded = () => {
        // The database did not exist; do not create Firebase's store on its behalf.
        open.transaction?.abort();
        resolve(undefined);
      };
      open.onsuccess = () => {
        const db = open.result;
        try {
          const tx = db.transaction('firebaseLocalStorage', 'readonly');
          const req = tx.objectStore('firebaseLocalStorage').get(key);
          req.onsuccess = () => {
            const rec = req.result as { value?: unknown } | undefined;
            db.close();
            resolve(rec?.value);
          };
          req.onerror = () => {
            db.close();
            resolve(undefined);
          };
        } catch {
          db.close();
          resolve(undefined);
        }
      };
    } catch {
      resolve(undefined);
    }
  });
}
